import ApexCharts from 'apexcharts'
import { activityChartOptions, categoriesDonutOptions, tagsBarOptions } from '../../modules/transaction_charts.js'

// The public demo: the transactions page of the product over an invented ledger. The ledger comes with the page,
// filtering, the list and the charts run here, with the same markup and chart options as the real page.
const root = document.getElementById('demo-root');

if (root) {
    const months = JSON.parse(root.dataset.months);
    const locale = root.dataset.locale || undefined;
    const pageSize = Number(root.dataset.pageSize) || 25;
    const headers = Object.fromEntries([...document.getElementById('demo-headers').content.querySelectorAll('[data-h]')]
        .map(el => [el.dataset.h, el.textContent]));
    const els = {
        month: document.getElementById('demo-month'),
        type: document.getElementById('demo-type'),
        search: document.getElementById('demo-search'),
        list: document.getElementById('demo-list'),
        drilldown: document.getElementById('demo-drilldown'),
        drilldownLabel: document.getElementById('demo-drilldown-label')
    };
    const state = { view: 'dates', type: '', search: '', day: null, page: 1, chart: 'activity' };
    const charts = {};

    const number = value => Number(value).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const escape = text => String(text ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const month = () => months.find(m => m.key === els.month.value) || months[0];

    // what the filters leave of the month; transfers are neither spending nor income
    const filtered = (withDay = true) => {
        const search = state.search.trim().toLowerCase();
        return month().transactions.filter(t =>
            (state.type === '' || String(t.type) === state.type)
            && (!withDay || !state.day || t.iso === state.day)
            && (!search || [t.tag, t.category, t.account].some(v => v && v.toLowerCase().includes(search))));
    };

    const amountClass = t => t.type === 0 ? 'text-primary' : (t.type === 1 ? 'text-success' : 'text-danger');
    const tagBadge = tag => `<span class="ms-details-tag-badge" title="${escape(tag)}"><span class="ms-tag-hash">#</span><span class="ms-tag-name">${escape(tag)}</span></span>`;

    function renderDates(rows) {
        const total = rows.length;
        const pages = Math.max(1, Math.ceil(total / pageSize));
        state.page = Math.min(state.page, pages);
        const first = (state.page - 1) * pageSize;
        const shown = rows.slice(first, first + pageSize);

        const body = shown.length ? shown.map(t => {
            const account = t.type === 0
                ? `<i class="mdi mdi-swap-horizontal text-primary me-1" title="${escape(root.dataset.labelTransfer)}"></i><span class="ms-tx-account-name">${escape(t.account)}</span>`
                : `<span class="ms-tx-account-name">${escape(t.account)}</span>`;
            const tags = t.tag ? `<div class="ms-tx-tags-wrap">${tagBadge(t.tag)}</div>` : '';
            return `<tr class="ms-transaction-row-main ${t.tag ? 'ms-transaction-row-main-with-details' : ''}">
                    <td class="align-middle"><span class="ms-tx-date">${escape(t.date)}</span></td>
                    <td class="ms-transaction-account-cell align-middle"><div class="d-flex align-items-center">${account}</div></td>
                    <td class="collapsable px-0 d-none d-lg-table-cell ms-transaction-tags-cell align-middle">${tags}</td>
                    <td class="text-end ps-0 align-middle ms-tx-amount ${amountClass(t)}">${number(t.amount)}</td>
                </tr>
                ${t.tag ? `<tr class="d-lg-none ms-transaction-mobile-details-row"><td colspan="4"><div class="ms-transaction-mobile-meta">${tags}</div></td></tr>` : ''}`;
        }).join('') : `<tr><td colspan="4" class="text-center text-muted py-4">${escape(root.dataset.labelEmpty)}</td></tr>`;

        const pageLinks = Array.from({ length: pages }, (_, i) => i + 1).map(p => p === state.page
            ? `<li class="page-item active"><p class="page-link">${p}</p></li>`
            : `<li class="page-item"><a class="page-link" href="javascript:void(0)" data-demo-page="${p}">${p}</a></li>`).join('');
        const showing = (root.dataset.labelShowing || '{0}-{1}/{2}')
            .replace('{0}', total ? first + 1 : 0).replace('{1}', Math.min(first + pageSize, total)).replace('{2}', total);

        els.list.innerHTML = `<div class="table-responsive ms-transactions-table-wrap">
                <table class="table table-hover ms-transactions-table">
                    <thead class="table-light"><tr>
                        <th class="align-middle">${escape(headers.date)}</th>
                        <th class="align-middle">${escape(headers.account)}</th>
                        <th class="align-middle px-0 d-none d-lg-table-cell">${escape(headers.tags)}</th>
                        <th class="align-middle ps-0 text-end">${escape(headers.amount)}</th>
                    </tr></thead>
                    <tbody>${body}</tbody>
                </table>
            </div>
            ${total ? `<div class="row mt-3">
                <div class="col-sm-12 col-md-5"><div class="dataTables_info" role="status" aria-live="polite">${escape(showing)}</div></div>
                <div class="col-sm-12 col-md-7"><nav><ul class="pagination justify-content-end mb-0">${pages > 1 ? pageLinks : ''}</ul></nav></div>
            </div>` : ''}`;
    }

    // by tags or by categories: only what has money in the month shows up
    function renderGroups(rows, byTag) {
        const groups = new Map();
        rows.filter(t => t.type !== 0).forEach(t => {
            const key = byTag ? t.tag : t.category;
            const group = groups.get(key) || { name: key, icon: t.icon, category: t.category, tags: new Set(), count: 0, total: 0 };
            group.tags.add(t.tag);
            group.count += 1;
            group.total += t.amount * t.type;
            groups.set(key, group);
        });
        const list = [...groups.values()].sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
        const sum = list.reduce((s, g) => s + Math.abs(g.total), 0) || 1;

        const body = list.length ? list.map(g => {
            const name = byTag
                ? tagBadge(g.name)
                : `<div class="d-flex align-items-center gap-2"><i class="${escape(g.icon)} font-size-16 text-primary"></i><span class="fw-medium">${escape(g.name)}</span></div>`;
            const detail = byTag
                ? `<span class="text-muted">${escape(g.category)}</span>`
                : `<div class="ms-tx-tags-wrap">${[...g.tags].map(tagBadge).join('')}</div>`;
            return `<tr>
                    <td class="align-middle">${name}<div class="d-lg-none mt-1 font-size-12">${detail}</div></td>
                    <td class="align-middle d-none d-lg-table-cell">${detail}</td>
                    <td class="align-middle text-end text-muted d-none d-sm-table-cell">${Math.round(Math.abs(g.total) / sum * 100)}%</td>
                    <td class="align-middle text-end ms-tx-amount ${g.total >= 0 ? 'text-success' : 'text-danger'}">${number(Math.abs(g.total))}<div class="text-muted font-size-11">${g.count} ${escape(root.dataset.labelOperations)}</div></td>
                </tr>`;
        }).join('') : `<tr><td colspan="4" class="text-center text-muted py-4">${escape(root.dataset.labelEmpty)}</td></tr>`;

        els.list.innerHTML = `<div class="table-responsive ms-transactions-table-wrap">
                <table class="table table-hover ms-transactions-table">
                    <thead class="table-light"><tr>
                        <th class="align-middle">${escape(byTag ? headers.tags : headers.category)}</th>
                        <th class="align-middle d-none d-lg-table-cell">${escape(byTag ? headers.category : headers.tags)}</th>
                        <th class="align-middle text-end d-none d-sm-table-cell">${escape(headers.share)}</th>
                        <th class="align-middle text-end">${escape(headers.amount)}</th>
                    </tr></thead>
                    <tbody>${body}</tbody>
                </table>
            </div>`;
    }

    function renderKpis() {
        const rows = filtered(false).filter(t => t.type !== 0);
        const incomes = rows.filter(t => t.type === 1).reduce((s, t) => s + t.amount, 0);
        const expenses = rows.filter(t => t.type === -1).reduce((s, t) => s + t.amount, 0);
        const net = incomes - expenses;
        document.getElementById('demo-kpi-incomes').textContent = '+ ' + number(incomes);
        document.getElementById('demo-kpi-expenses').textContent = '- ' + number(expenses);
        const netEl = document.getElementById('demo-kpi-net');
        netEl.textContent = (net >= 0 ? '+' : '') + number(net);
        netEl.className = 'kpi-value ' + (net >= 0 ? 'text-success' : 'text-danger');
        const flow = incomes + expenses || 1;
        document.getElementById('demo-cashflow-incomes').style.width = `${incomes / flow * 100}%`;
        document.getElementById('demo-cashflow-expenses').style.width = `${expenses / flow * 100}%`;
        document.querySelectorAll('[data-demo-budget]').forEach(el => el.classList.toggle('d-none', el.dataset.demoBudget !== month().key));
    }

    const draw = (id, options) => {
        charts[id]?.destroy();
        const el = document.getElementById(id);
        el.innerHTML = '';
        if (!options) {
            el.innerHTML = `<div class="text-center text-muted py-5 font-size-13">${escape(el.dataset.emptyTitle || root.dataset.labelEmpty)}</div>`;
            return;
        }
        charts[id] = new ApexCharts(el, options);
        charts[id].render();
    };

    // every day of the month, like the real chart: the net amount of the day and its average operation
    function renderActivity() {
        const m = month();
        const rows = filtered(false).filter(t => t.type !== 0);
        const [year, mon] = m.key.split('-').map(Number);
        const days = [], bars = [], line = [];
        for (let d = 1; d <= m.days; d++) {
            const iso = `${m.key}-${String(d).padStart(2, '0')}`;
            const ofDay = rows.filter(t => t.iso === iso).map(t => t.amount * t.type);
            const sum = ofDay.reduce((s, v) => s + v, 0);
            days.push(Date.UTC(year, mon - 1, d));
            bars.push(Math.abs(sum));
            line.push(ofDay.length ? Math.abs(sum / ofDay.length) : 0);
        }
        const el = document.getElementById('demo-activity-chart');
        draw('demo-activity-chart', activityChartOptions({
            days, bars, line,
            barLabel: el.dataset.barLabel,
            lineLabel: el.dataset.lineLabel,
            events: {
                dataPointSelection: (event, context, config) => {
                    if (config.seriesIndex !== 0) return;
                    const iso = new Date(days[config.dataPointIndex]).toISOString().slice(0, 10);
                    state.day = state.day === iso ? null : iso;
                    state.page = 1;
                    renderList();
                }
            }
        }));
    }

    // spending by default, income only when it is chosen, as on the real page
    const chartRows = () => filtered(false).filter(t => t.type === (state.type === '1' ? 1 : -1));
    const totals = key => [...chartRows().reduce((map, t) => map.set(t[key], (map.get(t[key]) || 0) + t.amount), new Map())]
        .sort((a, b) => b[1] - a[1]);

    function renderCategories() {
        const list = totals('category');
        const el = document.getElementById('demo-categories-chart');
        draw('demo-categories-chart', list.length ? categoriesDonutOptions({ labels: list.map(x => x[0]), series: list.map(x => x[1]), totalLabel: el.dataset.totalLabel }) : null);
    }

    function renderTags() {
        const list = totals('tag').slice(0, 12);
        const el = document.getElementById('demo-tags-chart');
        draw('demo-tags-chart', list.length ? tagsBarOptions({ categories: list.map(x => '#' + x[0]), data: list.map(x => x[1]), amountLabel: el.dataset.amountLabel }) : null);
    }

    function renderList() {
        const rows = filtered();
        if (state.view === 'dates') renderDates(rows);
        else renderGroups(rows, state.view === 'tags');
        els.drilldown.classList.toggle('d-none', !state.day);
        els.drilldown.classList.toggle('d-flex', !!state.day);
        if (state.day) {
            els.drilldownLabel.textContent = `${root.dataset.labelDay} ${month().transactions.find(t => t.iso === state.day)?.date || state.day}`;
        }
    }

    // a chart is drawn when its tab is visible, otherwise it would measure a hidden box
    function renderChart() {
        ({ activity: renderActivity, categories: renderCategories, tags: renderTags })[state.chart]();
    }

    function renderAll() {
        renderKpis();
        renderList();
        renderChart();
    }

    function setType(value) {
        state.type = value;
        state.day = null;
        state.page = 1;
        els.type.value = value;
        document.querySelectorAll('[data-demo-type]').forEach(b => {
            const on = b.dataset.demoType === value;
            b.classList.toggle('btn-primary', on);
            b.classList.toggle('active', on);
            b.classList.toggle('btn-light', !on);
        });
        renderAll();
    }

    els.month.addEventListener('change', () => { state.day = null; state.page = 1; renderAll(); });
    els.type.addEventListener('change', () => setType(els.type.value));
    document.querySelectorAll('[data-demo-type]').forEach(b => b.addEventListener('click', () => setType(b.dataset.demoType)));
    els.search.addEventListener('input', () => { state.search = els.search.value; state.page = 1; renderAll(); });
    document.getElementById('demo-drilldown-reset').addEventListener('click', () => { state.day = null; renderList(); });
    els.list.addEventListener('click', event => {
        const link = event.target.closest('[data-demo-page]');
        if (link) {
            state.page = Number(link.dataset.demoPage);
            renderList();
        }
    });
    document.querySelectorAll('[data-demo-view]').forEach(b => b.addEventListener('click', () => {
        document.querySelectorAll('[data-demo-view]').forEach(x => x.classList.toggle('active', x === b));
        state.view = b.dataset.demoView;
        state.page = 1;
        renderList();
    }));
    document.querySelectorAll('[data-demo-chart]').forEach(b => b.addEventListener('shown.bs.tab', () => { state.chart = b.dataset.demoChart; renderChart(); }));
    document.getElementById('demo-chart-collapse').addEventListener('shown.bs.collapse', renderChart);
    window.addEventListener('app:theme-changed', renderChart);

    renderAll();
}
